import { Fragment, createElement, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "services";

import { cn, createObjectBaseMatchVal, sanitizeObject } from "utils";
import { toast } from "react-toastify";

import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { mcSchema } from "schema";

import SidebarHeading from "components/SidebarHeading";
import FormGroupBtn from "components/FormGroupBtn";

import { motorCarrierForm } from "./formData";

const UpdateMotorCarrier = ({
  unmountCb,
  formValues,
  method,
  path,
  queryKey,
  heading,
  className,
  onClose,
  primaryButtonText,
}) => {
  const queryClient = useQueryClient();
  const { defaultValues, formInputs } = motorCarrierForm;

  useEffect(() => {
    return () => {
      unmountCb && unmountCb();
    };
  }, []);

  const { mutate, isPending } = useMutation({
    mutationFn: (values) => api[method]({ path, values }),
    onSuccess: () => {
      onClose();
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => toast.error(error),
  });

  const { control, formState, handleSubmit } = useForm({
    mode: "onChange",
    defaultValues: createObjectBaseMatchVal(defaultValues, formValues),
    resolver: yupResolver(mcSchema),
  });
  const { errors } = formState;

  const handleSubmitForm = handleSubmit((values) => {
    values = sanitizeObject(values);
    mutate(values);
  });

  return (
    <>
      <SidebarHeading onClose={onClose}>{heading}</SidebarHeading>

      <form className={cn("", className)} onSubmit={handleSubmitForm}>
        <div className="px-16 grid grid-cols-12 gap-16">
          {Object.values(formInputs).map((input) => {
            const { type, Component, name, inputProps, withValue } = input;
            let render = null;
            switch (type) {
              case "input":
                render = (
                  <Controller
                    name={name}
                    control={control}
                    render={({ field }) => {
                      const { ref, value, ...restField } = field;
                      const errorMessage = errors?.[name]?.message;
                      const val = withValue ? withValue(value) : { value };
                      return createElement(Component, {
                        id: name,
                        light: true,
                        invalid: Boolean(errorMessage),
                        invalidText: errorMessage,
                        ...val,
                        ...inputProps,
                        ...restField,
                      });
                    }}
                  />
                );
                break;
              case "groupName":
                render = (
                  <span className={inputProps.className}>
                    {inputProps.text}
                  </span>
                );
                break;
              default:
            }

            return <Fragment key={name}>{render}</Fragment>;
          })}
        </div>

        <FormGroupBtn
          secondaryButtonText="Cancel"
          primaryButtonText={primaryButtonText}
          onClose={onClose}
          isPending={isPending}
        />
      </form>
    </>
  );
};

export default UpdateMotorCarrier;
